import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import { PdfViewer } from "@/components/course/PdfViewer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Music, Sparkles } from "lucide-react";

interface Kinderlied {
  id: string;
  slug: string;
  titel: string;
  kurzbeschreibung: string | null;
  liedtext: string | null;
  pdf_url: string | null;
  kategorie: string | null;
  saison: string | null;
  paedagogischer_kontext: string | null;
}

/**
 * SEO-Detailseite für ein einzelnes gemeinfreies Kinderlied.
 * Enthält Liedtext, eingebettete Noten-PDF und pädagogischen Kontext.
 */
const KinderliedDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [lied, setLied] = useState<Kinderlied | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("kinderlieder")
        .select(
          "id, slug, titel, kurzbeschreibung, liedtext, pdf_url, kategorie, saison, paedagogischer_kontext"
        )
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("[KinderliedDetail] Fehler:", error);
      }
      if (!data) {
        setNotFound(true);
      } else {
        setLied(data);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 text-center text-muted-foreground">
        Lied wird geladen…
      </div>
    );
  }

  if (notFound || !lied) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Lied nicht gefunden</h1>
        <Button asChild>
          <Link to="/kinderlieder">Zurück zur Übersicht</Link>
        </Button>
      </div>
    );
  }

  const description =
    lied.kurzbeschreibung ??
    `${lied.titel} – traditionelles Kinderlied mit Text, Noten und pädagogischen Tipps für Kita und Grundschule.`;

  const musicJsonLd = {
    "@context": "https://schema.org",
    "@type": "MusicComposition",
    name: lied.titel,
    musicCompositionForm: lied.kategorie ?? "Volkslied",
    inLanguage: "de",
    lyrics: lied.liedtext
      ? { "@type": "CreativeWork", text: lied.liedtext }
      : undefined,
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Kinderlieder",
        item: "https://www.musikonzept.de/kinderlieder",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: lied.titel,
        item: `https://www.musikonzept.de/kinderlieder/${lied.slug}`,
      },
    ],
  };

  return (
    <>
      <SEO
        title={`${lied.titel} – Liedtext, Noten & pädagogische Tipps | MusiKonzept`}
        description={description.slice(0, 158)}
        path={`/kinderlieder/${lied.slug}`}
        type="article"
        jsonLd={[musicJsonLd, breadcrumbJsonLd]}
      />

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button asChild variant="ghost" size="sm" className="mb-6">
          <Link to="/kinderlieder">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Alle Kinderlieder
          </Link>
        </Button>

        <header className="mb-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Music className="h-4 w-4" />
            {lied.kategorie ?? "Kinderlied"}
            {lied.saison ? <span>· {lied.saison}</span> : null}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {lied.titel}
          </h1>
          {lied.kurzbeschreibung && (
            <p className="text-lg text-muted-foreground">{lied.kurzbeschreibung}</p>
          )}
        </header>

        {lied.liedtext && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Liedtext</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="whitespace-pre-wrap font-sans text-base leading-relaxed">
                {lied.liedtext}
              </pre>
            </CardContent>
          </Card>
        )}

        {lied.pdf_url && (
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Noten & Akkorde</h2>
            <PdfViewer
              url={lied.pdf_url}
              fileName={`${lied.titel}.pdf`}
              allowDownload={false}
              showEmbed
            />
          </section>
        )}

        {lied.paedagogischer_kontext && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Pädagogischer Kontext</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-neutral max-w-none whitespace-pre-wrap text-base leading-relaxed">
                {lied.paedagogischer_kontext}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="bg-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Mehr als 500 Kinderlieder mit Noten, Audio & Video
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>
              In der MusiKonzept-Mitgliedschaft findest du eine umfassende
              Kinderlieder-Bibliothek mit Audio-Aufnahmen, Video-Anleitungen und
              didaktischen Materialien für deine Kita-Praxis.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <Link to="/preise">Mitgliedschaft entdecken</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/termine">Liederwerkstatt-Termine</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </>
  );
};

export default KinderliedDetail;
